import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CallEvent} from "../../../../units/models/call-event";
import {UnitCallEventsFilters} from "../../../../units/models/unit-call-events-filters";
import {FarfisaDevice} from "../../../models/devices/farfisa_device";
import {UnitsManager} from "../../../../units/services/units-manager";
import {Observable, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, skip, takeUntil} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {selectCurrentCall} from '../../../../state/call/current-call.selector';

@Component({
    selector: 'app-call-events-card',
    templateUrl: './call-events-card.component.html',
    styleUrl: './call-events-card.component.scss'
})
export class CallEventsCardComponent implements OnInit, OnDestroy {

    @ViewChild('popupScrollView', { static: false }) set popupScrollViewSet(scrollView: any) {
        scrollView?.instance.release(true);
        this.popupScrollView = scrollView;
    }

    @Input() eventsProvider?: (args: { installationId: string, entityId: string, pagination?: any, filters?: UnitCallEventsFilters }) => Observable<CallEvent[]>;

    @Input() set devices(value: FarfisaDevice[] | undefined) {
        this.isBusy = true;
        if (!value)
            return;
        this.eventDevices = value;
        this.refreshPreviewEvents();
    }

    @Input() units: { id: string, name?: string }[] = [];

    @Input() pbxs: { id: string, name?: string }[] = [];

    @Input() entityId: string | null = null;

    @Input() installationId: string | null = null;

    isBusy: boolean = false;

    eventDevices: FarfisaDevice[] | undefined = undefined;
    popupScrollView: any;
    callEvents: CallEvent[] = [];
    popupCallEvents: CallEvent[] = [];
    readonly popupEventsPageSize = 10;
    expandedEventId: string | null = null;
    expandedPopupEventId: string | null = null;
    hasMoreEvents: boolean = false;
    showEventPopup: boolean = false;
    popupEventsPage = 1;
    popupEventsHasMore = false;
    isEventPopupLoading: boolean = false;
    currentFilters: UnitCallEventsFilters = {};

    private destroy$ = new Subject<void>();

    constructor(private unitsManager: UnitsManager, private store: Store) { }

    ngOnInit() {
        // Refresh the recent events list whenever the call state changes
        // (e.g. call started, answered, held, bridged, ended) so the UI
        // stays in sync with the backend. A 1s debounce is needed because
        // the cloud takes some time to persist the updated call event.
        this.store.select(selectCurrentCall).pipe(
            map(call => call?.mainLeg?.status + '|' + call?.pipLeg?.status),
            distinctUntilChanged(),
            skip(1),
            debounceTime(1000),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            if (this.eventDevices?.length) {
                this.refreshPreviewEvents();
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    callEventClicked(eventId: string) {
        this.expandedEventId = this.expandedEventId === eventId ? null : eventId;
    }

    popupCallEventClicked(eventId: string) {
        this.expandedPopupEventId = this.expandedPopupEventId === eventId ? null : eventId;
    }

    showMoreEvents() {
        this.expandedEventId = null;
        this.expandedPopupEventId = null;
        this.showEventPopup = true;
        this.resetPopupEvents();
        this.loadPopupEventsPage(null);
    }

    resetPopupEvents() {
        this.popupEventsPage = 1;
        this.popupCallEvents = [];
        this.popupEventsHasMore = false;
    }

    onFiltersChanged(filters: UnitCallEventsFilters) {
        this.currentFilters = filters;
        this.resetPopupEvents();
        this.loadPopupEventsPage(null);
    }

    loadPopupEventsPage(scrollViewEvent: any) {
        this.isEventPopupLoading = true;
        this.fetchEvents({ index: this.popupEventsPage, size: this.popupEventsPageSize }, this.currentFilters)
            .subscribe((callEvents: CallEvent[]) => {
                this.isEventPopupLoading = false;
                const mappedEvents = callEvents.map(event => ({
                    ...event,
                    deviceName: this.resolveRemoteEntityName(event),
                    isOutgoing: event.caller?.entity?.id === this.entityId
                }));
                this.popupCallEvents.push(...mappedEvents);
                if (scrollViewEvent) {
                    scrollViewEvent.component.release(true);
                }

                this.popupEventsHasMore = callEvents.length === this.popupEventsPageSize;

                if (!callEvents?.length || !this.popupEventsHasMore) {
                    this.popupScrollView.component.release(true);
                } else {
                    setTimeout(() => this.popupScrollView?.instance.release(false), 0);
                }

                if (this.popupEventsHasMore) {
                    this.popupEventsPage++;
                }
            }, () => {
                this.isEventPopupLoading = false;
            });
    }

    onPopupScrollEnd(e: any) {
        e.component.release(true);
        if (this.popupEventsHasMore) {
            this.loadPopupEventsPage(e);
        }
    }

    private refreshPreviewEvents(): void {
        this.fetchEvents({ index: 1, size: 5 }, undefined).subscribe(
            (callEvents: CallEvent[]) => {
                this.unitCallEventsRetrieved(callEvents);
                this.isBusy = false;
            }
        );
    }

    private unitCallEventsRetrieved(callEvents: CallEvent[]) {
        if (!callEvents.length) {
            return;
        }

        const mapped = callEvents.map(event => ({
            ...event,
            deviceName: this.resolveRemoteEntityName(event),
            isOutgoing: event.caller?.entity?.id === this.entityId
        }));

        if (this.eventsChanged(this.callEvents, mapped)) {
            this.callEvents = mapped;
        }

        this.hasMoreEvents = !(callEvents.length < 5);
    }

    /**
     * Determine who the "remote party" is for this event:
     * if I'm the caller → show the callee; otherwise → show the caller.
     * Then resolve the name from devices (doorstations) or units.
     */
    private resolveRemoteEntityName(event: CallEvent): string | undefined {
        const iAmTheCaller = event.caller?.entity?.id === this.entityId;
        const remoteEntity = iAmTheCaller ? event.callee : event.caller?.entity;
        if (!remoteEntity?.id) return undefined;

        const device = (this.eventDevices ?? []).find(d => d.id === remoteEntity.id);
        if (device) return device.name;

        const unit = (this.units ?? []).find(u => u.id === remoteEntity.id);
        if (unit?.name) return unit.name;

        const pbx = (this.pbxs ?? []).find(p => p.id === remoteEntity.id);
        if (pbx?.name) return pbx.name;

        return undefined;
    }

    private eventsChanged(current: CallEvent[], incoming: CallEvent[]): boolean {
        if (current.length !== incoming.length) return true;
        return current.some((e, i) => e.id !== incoming[i].id || e.state !== incoming[i].state);
    }

    private fetchEvents(pagination?: any, filters?: UnitCallEventsFilters): Observable<CallEvent[]> {
        const installationId = this.installationId;
        const entityId = this.entityId;
        if (!installationId || !entityId) {
            throw new Error('CallEventsCardComponent: missing required data');
        }

        return this.eventsProvider!({ installationId, entityId, pagination, filters });
    }

}

